/**
 * NetCapitalGainModal — Round 92o++++++++N++++++++++ (May 2026).
 *
 * Clickable expansion of the "Net Capital Gain" tile in the My
 * Portfolio summary card. Shows the explicit formula at the top so
 * users understand what they're looking at, then breaks the number
 * down component-by-component (Total Value − Net Contributions −
 * Dividends Collected) with each input visible.
 *
 * Read-only: no inputs, no axios calls. All values are passed in
 * as props from PortfolioSummary's existing `data` payload so this
 * modal never re-fetches and can't desync from the tile above it.
 *
 * The big idea: dividend investors confuse "Net Capital Gain" with
 * "Total Return". This modal makes the difference legible — net
 * capital gain is pure share-price appreciation on outside-money
 * after subtracting cash put in AND dividends collected, while
 * total return is gain + dividends.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { ArrowUpRight, ArrowDownRight, TrendingUp, DollarSign, Coins, Receipt, Clock } from 'lucide-react';
import { useActivePortfolioId } from '@/components/PortfolioSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtCurrency(n, opts = {}) {
  const v = Number(n) || 0;
  const sign = opts.signed ? (v >= 0 ? '+' : '−') : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function NetCapitalGainModal({
  open,
  onClose,
  totalValue = 0,
  netContributions = 0,
  dividendsCollected = 0,
  totalContributions = 0,
  totalWithdrawals = 0,
  // Round P108u (Feb 2026) — fallback mode. When the tile falls
  // back to plain Unrealized G/L (no contribution/dividend history
  // tracked), the parent passes fallbackMode=true plus positions
  // value + cost basis so this modal renders a simpler breakdown
  // instead of the full Net Capital Gain math (which would be
  // nonsense without contribution data).
  fallbackMode = false,
  positionsValue = 0,
  totalCostBasis = 0,
}) {
  const ncg = fallbackMode
    ? (Number(positionsValue) || 0) - (Number(totalCostBasis) || 0)
    : (Number(totalValue) || 0) - (Number(netContributions) || 0);
  const positive = ncg >= 0;
  // Feb 2026 (Round P111a) — Under the new formula NCG already
  // includes dividends (they're inside Total Value, either as cash
  // or as new DRIP shares), so the previous "Total Return = NCG +
  // dividends" summing double-counts them. Keep the "For
  // comparison" block but reframe it as unrealized-only.
  const unrealizedOnly = (Number(positionsValue) || 0) - (Number(totalCostBasis) || 0);
  const uPositive = unrealizedOnly >= 0;
  // All-time % return on outside money — NCG / Net Contributions.
  // Round 92o++++++++N++++++++++ (May 2026) — added per user request
  // so the modal answers the natural "what % is that?" follow-up
  // without forcing them to open a calculator.
  const ncgPct = fallbackMode
    ? ((Number(totalCostBasis) || 0) > 0 ? (ncg / Number(totalCostBasis)) * 100 : null)
    : ((Number(netContributions) || 0) > 0 ? (ncg / Number(netContributions)) * 100 : null);

  // Round 92o++++++++N++++++++++ (May 2026) — Money-Weighted IRR
  // fetched on modal open. Backend reads cash_transactions and runs
  // a bisection XIRR solver. We only fire when the modal is OPEN so
  // it doesn't tax the dashboard on every summary render.
  const [irr, setIrr] = useState({ loading: false, data: null });
  const { activeId: irrPortfolioId } = useActivePortfolioId();
  useEffect(() => {
    if (!open || fallbackMode) return;
    let cancelled = false;
    setIrr({ loading: true, data: null });
    axios.get(`${API}/portfolio/irr`, {
      params: { total_value: Number(totalValue) || 0, ...(irrPortfolioId ? { portfolio_id: irrPortfolioId } : {}) },
      withCredentials: true,
    })
      .then((r) => { if (!cancelled) setIrr({ loading: false, data: r.data }); })
      .catch(() => { if (!cancelled) setIrr({ loading: false, data: null }); });
    return () => { cancelled = true; };
  }, [open, totalValue, fallbackMode, irrPortfolioId]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-lg sm:max-w-xl w-[95vw] sm:w-auto max-h-[88vh] overflow-y-auto pb-24 sm:pb-4"
        data-testid="net-capital-gain-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className={`w-5 h-5 ${positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`} />
            {fallbackMode ? 'Unrealized G/L' : 'Net Capital Gain'}
          </DialogTitle>
          <DialogDescription>
            {fallbackMode
              ? "How much your positions have gained (or lost) versus what you paid for them. This is the same number you'll see on Tax Lots, Tax Account Audit, and Year-End Snapshot — it becomes real cash only when you sell. To unlock the fuller Net Capital Gain breakdown (which also nets out outside deposits and dividends collected), log a cash deposit in the Capital Tracker."
              : <>Unrealized share-price appreciation on your outside money — what your remaining positions are worth beyond the cash you put in and the dividends already paid to you. When you sell, the realized gain moves into your Cash Balance, so this number drops even though your total wealth hasn&apos;t changed.</>}
          </DialogDescription>
        </DialogHeader>

        {/* Formula card. Keeps the math explicit so power users can
            check the math against the values below without parsing
            prose. */}
        <div
          className="rounded-lg border bg-muted/30 p-3 text-center"
          data-testid="ncg-formula"
        >
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">
            Formula
          </p>
          {fallbackMode ? (
            <>
              <p className="font-mono text-xs sm:text-sm font-semibold leading-relaxed break-words">
                Market Value
                <span className="text-rose-600 dark:text-rose-400"> − </span>
                Cost Basis
              </p>
              <p className="font-mono text-[11px] sm:text-xs text-muted-foreground mt-1">
                = Unrealized G/L
              </p>
            </>
          ) : (
            <>
              <p className="font-mono text-xs sm:text-sm font-semibold leading-relaxed break-words">
                Total Value
                <span className="text-rose-600 dark:text-rose-400"> − </span>
                Net Contributions
              </p>
              <p className="font-mono text-[11px] sm:text-xs text-muted-foreground mt-1">
                = Net Capital Gain
              </p>
            </>
          )}
        </div>

        {/* Breakdown — one row per input, signed so the running math is
            obvious. */}
        <div className="space-y-2">
          {fallbackMode ? (
            <>
              <RowItem
                icon={DollarSign}
                label="Market Value"
                hint="Current live market value of every share you hold across this portfolio."
                value={fmtCurrency(positionsValue)}
                tone="neutral"
                op="="
                testid="ncg-row-market-value"
              />
              <RowItem
                icon={Receipt}
                label="Cost Basis"
                hint="What you paid for every share still open (shares × cost per share, summed across every lot)."
                value={fmtCurrency(totalCostBasis)}
                tone="rose"
                op="−"
                testid="ncg-row-cost-basis"
              />
            </>
          ) : (
            <>
              <RowItem
                icon={DollarSign}
                label="Total Value"
                hint="Current market value of every share you hold, marked at the latest live price."
                value={fmtCurrency(totalValue)}
                tone="neutral"
                op="="
                testid="ncg-row-total-value"
              />
              <RowItem
                icon={Receipt}
                label="Net Contributions"
                hint={
                  totalWithdrawals
                    ? `${fmtCurrency(totalContributions)} deposits − ${fmtCurrency(totalWithdrawals)} withdrawals = outside money still in. Sales don't count as withdrawals — proceeds stay inside as Cash Balance.`
                    : `Outside money you have put into this portfolio (deposits − any withdrawals). Sales don't count as withdrawals — proceeds stay inside as Cash Balance.`
                }
                value={fmtCurrency(netContributions)}
                tone="rose"
                op="−"
                testid="ncg-row-net-contributions"
              />
            </>
          )}
        </div>

        {/* Bottom result */}
        <div
          className={`rounded-lg border-2 p-3 text-center ${
            positive
              ? 'border-emerald-300/60 dark:border-emerald-700/60 bg-emerald-50/40 dark:bg-emerald-950/20'
              : 'border-rose-300/60 dark:border-rose-700/60 bg-rose-50/40 dark:bg-rose-950/20'
          }`}
          data-testid="ncg-result"
        >
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">
            {fallbackMode ? 'Unrealized G/L' : 'Net Capital Gain'}
          </p>
          <p className={`font-mono text-2xl font-bold ${positive ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {positive ? <ArrowUpRight className="inline w-5 h-5 mb-1 mr-1" /> : <ArrowDownRight className="inline w-5 h-5 mb-1 mr-1" />}
            {fmtCurrency(ncg, { signed: true })}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {fallbackMode
              ? "Paper gain only — becomes real cash on the day you sell. If you'd like this tile to also net out cash deposits and dividends already received (the true Net Capital Gain), track them under Capital Tracker."
              : <>How much your total wealth (positions + cash + reinvested dividends) has grown vs. the out-of-pocket money you actually contributed. Same headline number your broker shows as All-Time gain.</>}
          </p>
        </div>

        {/* % return on outside money — only when there's actual
            outside money in (avoids divide-by-zero for hypothetical
            empty portfolios). */}
        {ncgPct !== null && (
          <p
            className="text-center text-xs sm:text-sm font-mono"
            data-testid="ncg-percent-return"
          >
            <span className={`font-bold ${positive ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {ncgPct >= 0 ? '+' : '−'}{Math.abs(ncgPct).toFixed(1)}%
            </span>
            <span className="text-muted-foreground"> {fallbackMode ? 'return on cost basis' : 'return on outside money'}</span>
          </p>
        )}

        {/* Money-Weighted IRR — annualised return on every dollar
            you've actually put in. Fetched async; hidden when the
            holding window is too short to be meaningful (<~5 wks). */}
        {irr.data && (
          <div
            className="rounded-md border bg-muted/20 px-3 py-2 text-center"
            data-testid="ncg-irr-block"
          >
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              Annualised IRR
            </p>
            {irr.data.irr_pct !== null && irr.data.irr_pct !== undefined ? (
              <p className="font-mono text-sm sm:text-base mt-0.5 break-words">
                <span className={`font-bold ${irr.data.irr_pct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {irr.data.irr_pct >= 0 ? '+' : '−'}{Math.abs(irr.data.irr_pct).toFixed(1)}%/yr
                </span>
                <span className="text-muted-foreground">
                  {' '}over {irr.data.years_elapsed?.toFixed?.(1) || irr.data.years_elapsed} yrs
                </span>
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground italic mt-0.5 leading-snug">
                {irr.data.skip_reason || 'IRR will appear once your portfolio has more history.'}
              </p>
            )}
          </div>
        )}
        {irr.loading && (
          <p className="text-center text-[10px] text-muted-foreground italic">
            Computing annualised IRR…
          </p>
        )}

        {/* Feb 2026 (Round P111a) — Unrealized-only contrast block.
            Under the new NCG formula, Total Value − Net Contributions
            IS the total return (dividends and realized gains are
            already inside Total Value). This block breaks out just
            the paper/unrealized portion for tax-planning clarity:
            "how much of my NCG is still tied up in unsold shares?"
            Only rendered when the numbers actually differ (i.e.,
            the user has realized gains or received dividends). */}
        {!fallbackMode && (Math.abs(ncg - unrealizedOnly) > 0.01) && (Number(totalCostBasis) || 0) > 0 && (
          <div
            className="rounded-lg border border-dashed p-2.5 text-center"
            data-testid="ncg-unrealized-only"
          >
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">
              Of which — Unrealized Only
            </p>
            <p className="font-mono text-xs sm:text-sm break-words leading-relaxed">
              <span className={uPositive ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-rose-600 dark:text-rose-400 font-bold'}>
                {fmtCurrency(unrealizedOnly, { signed: true })}
              </span>
              <span className="text-muted-foreground"> = {fmtCurrency(positionsValue)} market value − {fmtCurrency(totalCostBasis)} cost basis</span>
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Net Capital Gain above includes realized gains + dividends already in your cash balance. This row shows only the paper gain still riding in your open positions — the part that will convert to cash when you sell.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RowItem({ icon: Icon, label, hint, value, tone, op, testid }) {
  const tonePill =
    tone === 'rose'
      ? 'text-rose-700 dark:text-rose-400 bg-rose-50/60 dark:bg-rose-950/30 border-rose-300/60 dark:border-rose-800/60'
      : 'text-foreground bg-muted/30 border-border';
  return (
    <div
      className="flex items-start gap-3 rounded-md border bg-background/50 p-2.5"
      data-testid={testid}
    >
      <div className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border ${tonePill}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-bold">
            <span className="font-mono text-[11px] text-muted-foreground mr-1">{op}</span>
            {label}
          </span>
          <span className={`font-mono text-sm font-semibold ${tone === 'rose' ? 'text-rose-700 dark:text-rose-400' : 'text-foreground'}`}>
            {value}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
          {hint}
        </p>
      </div>
    </div>
  );
}
